import React from 'react'
import styled from 'styled-components'
import { X } from 'lucide-react'
import { MobileColors } from '../../styles/mobileTokens'

interface MobileTempDetailModalProps {
  isOpen: boolean
  onClose: () => void
  type: 'company' | 'door'
  id: string | number
  name?: string
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${MobileColors.textPrimary};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${MobileColors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${MobileColors.textPrimary};
  }
`

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${MobileColors.textSecondary};
  text-transform: uppercase;
`

const Value = styled.div`
  font-size: 16px;
  color: ${MobileColors.textPrimary};
  font-family: monospace;
`

const Note = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: ${MobileColors.backgroundCard};
  border-radius: 8px;
  font-size: 14px;
  color: ${MobileColors.textSecondary};
  line-height: 1.5;
`

export const MobileTempDetailModal: React.FC<MobileTempDetailModalProps> = ({
  isOpen,
  onClose,
  type,
  id,
  name,
}) => {
  if (!isOpen) return null

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {type === 'company' ? 'Company Details' : 'Location Details'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {name && (
            <InfoRow>
              <Label>Name</Label>
              <Value>{name}</Value>
            </InfoRow>
          )}
          <InfoRow>
            <Label>{type === 'company' ? 'Company ID' : 'Door ID'}</Label>
            <Value>{id}</Value>
          </InfoRow>
          <Note>
            Full {type === 'company' ? 'company' : 'location'} detail pages are
            coming soon to mobile. For now, please use the desktop version to
            view complete details.
          </Note>
        </ModalBody>
      </ModalContent>
    </Overlay>
  )
}
